import { create } from 'zustand';
import { AlertRuleEvent } from '@/types/event';
import { webSocketService, WSMessageType } from '@/services/websocket';
import { playAlertChime } from '@/utils/alertChime';
import { selectFeedPanelVisible, useEventFeedStore } from './event-feed.store';

export const ALERT_HIGHLIGHT_WINDOW_MS = 8000;
const ARRIVAL_PRUNE_BUFFER_MS = 100;

export const isAlertCovered = (alert: AlertRuleEvent): boolean => {
    const feedState = useEventFeedStore.getState();
    if (!selectFeedPanelVisible(feedState)) return false;

    const { context } = feedState;
    switch (context?.type) {
        case 'recentAlerts':
        case 'alerts':
            return true;
        case 'alertRule':
            return context.alertRuleId === alert.alertRuleId;
        case 'sourceAlerts':
            return context.sourceId === alert.event.sourceId;
        default:
            return false;
    }
};

export const alertKey = (alert: AlertRuleEvent): string =>
    `${alert.alertRuleId}-${alert.event.id}`;

export const isAlertNew = (
    arrivals: Record<string, number>,
    key: string,
): boolean => {
    const arrival = arrivals[key];
    return (
        arrival !== undefined &&
        Date.now() - arrival < ALERT_HIGHLIGHT_WINDOW_MS
    );
};

export function mergeAlertEvents(
    incoming: AlertRuleEvent[],
    existing: AlertRuleEvent[],
    itemKey: (alert: AlertRuleEvent) => string,
    limit = Infinity,
): AlertRuleEvent[] {
    const combined = [...incoming, ...existing].sort(
        (a, b) => (b.event.createdAt ?? 0) - (a.event.createdAt ?? 0),
    );
    const seenKeys = new Set<string>();
    const result: AlertRuleEvent[] = [];
    for (const alert of combined) {
        if (result.length >= limit) break;
        const key = itemKey(alert);
        if (!seenKeys.has(key)) {
            seenKeys.add(key);
            result.push(alert);
        }
    }
    return result;
}

const prunedArrivals = (
    arrivals: Record<string, number>,
    now: number,
): Record<string, number> =>
    Object.fromEntries(
        Object.entries(arrivals).filter(
            ([, time]) => now - time < ALERT_HIGHLIGHT_WINDOW_MS,
        ),
    );

interface AlertsState {
    recentAlerts: AlertRuleEvent[];
    notificationAlerts: AlertRuleEvent[];
    alertArrivals: Record<string, number>;
    alertsEnabled: boolean;
    setAlertsEnabled: (enabled: boolean) => void;
    setAlerts: (alerts: AlertRuleEvent[]) => void;
    receiveAlert: (alert: AlertRuleEvent) => void;
    removeNotificationAlert: (notificationKey: string) => void;
    removeAlertsBySourceId: (sourceId: string) => void;
    clearNotificationAlerts: () => void;
    clearAlerts: () => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
    recentAlerts: [],
    notificationAlerts: [],
    alertArrivals: {},
    alertsEnabled: true,

    setAlertsEnabled: (enabled) =>
        set(
            enabled
                ? { alertsEnabled: true }
                : {
                      alertsEnabled: false,
                      recentAlerts: [],
                      notificationAlerts: [],
                      alertArrivals: {},
                  },
        ),

    setAlerts: (alerts) => set({ recentAlerts: alerts }),

    receiveAlert: (alert) => {
        const state = get();
        const exists = state.recentAlerts.some(
            (a) => alertKey(a) === alertKey(alert),
        );
        if (exists) return;

        const now = Date.now();
        const notificationAlerts = isAlertCovered(alert)
            ? state.notificationAlerts
            : [...state.notificationAlerts, alert];

        set({
            recentAlerts: [alert, ...state.recentAlerts],
            notificationAlerts,
            alertArrivals: {
                ...prunedArrivals(state.alertArrivals, now),
                [alertKey(alert)]: now,
            },
        });
        playAlertChime();
    },

    removeNotificationAlert: (notificationKey) =>
        set((state) => ({
            notificationAlerts: state.notificationAlerts.filter(
                (alert) => alertKey(alert) !== notificationKey,
            ),
        })),

    removeAlertsBySourceId: (sourceId) =>
        set((state) => ({
            recentAlerts: state.recentAlerts.filter(
                (alert) => alert.event.sourceId !== sourceId,
            ),
            notificationAlerts: state.notificationAlerts.filter(
                (alert) => alert.event.sourceId !== sourceId,
            ),
        })),

    clearNotificationAlerts: () => set({ notificationAlerts: [] }),

    clearAlerts: () =>
        set({ recentAlerts: [], notificationAlerts: [], alertArrivals: {} }),
}));

let pendingArrivalPrune: ReturnType<typeof setTimeout> | null = null;

const scheduleArrivalPrune = () => {
    if (pendingArrivalPrune) return;
    pendingArrivalPrune = setTimeout(() => {
        pendingArrivalPrune = null;
        useAlertsStore.setState((state) => {
            const arrivals = prunedArrivals(state.alertArrivals, Date.now());
            return Object.keys(arrivals).length ===
                Object.keys(state.alertArrivals).length
                ? state
                : { alertArrivals: arrivals };
        });
        if (Object.keys(useAlertsStore.getState().alertArrivals).length > 0) {
            scheduleArrivalPrune();
        }
    }, ALERT_HIGHLIGHT_WINDOW_MS + ARRIVAL_PRUNE_BUFFER_MS);
};

if (typeof window !== 'undefined') {
    webSocketService.on<AlertRuleEvent>(WSMessageType.ALERT, (alert) => {
        if (!useAlertsStore.getState().alertsEnabled) return;
        useAlertsStore.getState().receiveAlert(alert);
        scheduleArrivalPrune();
    });
}
